/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { memo, useRef } from "react";
import type { FunctionComponent, JSX } from "react";

import type { SeeMoreTileProps } from "./SeeMoreTileProps";
import { useAccessibilityFocusSync } from "../../hooks/useAccessibilityFocusSync";
import { selectIsTvDevice, useUnitySelector } from "../../../viewModel";
import "./SeeMoreTile.scss";

/**
 * The tile that closes a carousel and opens its full grid page.
 *
 * Deliberately built on `Tile`'s own class structure rather than on `Tile`:
 * it has no feed item, no image, no badges and no progress bar, so reusing the
 * component would mean threading a "this one is not content" flag through
 * every branch of it. Borrowing the classes instead is what keeps the two the
 * same size — `tile--<template>` carries the width and the aspect ratio, so
 * this tile tracks any change to the template's dimensions for free, and the
 * `tile--smart-tv.tile--focused` focus ring (see Tile.scss) lands on it
 * exactly as it lands on a real tile, since it shares that class too.
 *
 * The variant class is built as `"tile--" + template` — the SAME derivation
 * `Tile` uses — so both resolve to the identical CSS variant (e.g.
 * `tile--poster_horizontal`). Deriving it via a separate mapper instead risked
 * emitting a class with no matching selector (e.g. `tile--poster`), which
 * silently left the tile at the base 16∶9 default while its neighbours were
 * 2∶3 — the shape/alignment mismatch this fixes.
 *
 * The empty `tile__info` block is not filler: it reserves the caption height
 * its neighbours occupy, so the rail's tiles keep a common baseline.
 */
const SeeMoreTileComponent: FunctionComponent<SeeMoreTileProps> = (
  props: SeeMoreTileProps
): JSX.Element => {
  const {
    label,
    template,
    isFocused,
    isGhostHovered,
    onFocus,
    onClick,
    onMouseEnter,
    onMouseLeave,
    onKeyDown,
    isAccessibilityFocused,
    accessibilityLabelPrefix
  } = props;

  // VoiceView (opt-in per rail): real DOM focus follows the D-pad focus.
  const tileRef = useRef<HTMLDivElement>(null);
  useAccessibilityFocusSync(tileRef, isAccessibilityFocused === true);

  const isSmartTV = useUnitySelector(selectIsTvDevice);

  const className: string =
    "tile carousel-see-more tile--" +
    template +
    (isSmartTV ? " tile--smart-tv" : "") +
    (isFocused ? " tile--focused carousel-see-more--focused" : "") +
    (isGhostHovered === true ? " tile--ghost-hovered" : "");

  return (
    <div
      ref={tileRef}
      className={className}
      tabIndex={0}
      role="button"
      aria-label={
        accessibilityLabelPrefix !== undefined &&
        accessibilityLabelPrefix !== ""
          ? accessibilityLabelPrefix + ", " + label
          : label
      }
      onFocus={onFocus}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={onKeyDown}
    >
      <div className="tile__image-container">
        <div className="carousel-see-more__inner">
          <span className="carousel-see-more__label">{label}</span>
        </div>
      </div>
      {isFocused && <div className="tile__info"></div>}
    </div>
  );
};

/**
 * Memoized for the same reason `Tile` is: a carousel re-renders on every focus
 * move, and this tile's props only change when the label or the template does.
 */
const SeeMoreTile = memo(SeeMoreTileComponent);

export default SeeMoreTile;
